import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { Badge, EmptyIndicator, FlexContainer } from 'components/common';
import {
  Box,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { StatCount } from 'components/common/stats';

import type { UserCustomAttribute } from 'shared-types';
import type {
  FormValuesInUsersImportCsv,
  UsersDataInUsersImportCsv
} from './UsersImportCsv';

interface Props {
  accountType: FormValuesInUsersImportCsv['accountType'];
  userCustomAttributes: UserCustomAttribute[];
  usersData: UsersDataInUsersImportCsv;
}

const UsersImportCsvPreview = ({
  accountType,
  userCustomAttributes,
  usersData
}: Props) => {
  const { language, messages } = useReduxState();

  const validRows =
    usersData?.filter((user) => user.status === 'ok')?.length || 0;
  const updateRows =
    usersData?.filter((user) => user.status === 'update')?.length || 0;
  const invalidRows =
    usersData?.filter((user) => user.status === 'invalid')?.length || 0;
  const ignoreRows =
    usersData?.filter(
      (user) => user.status === 'ignore' || user.status === 'duplicate'
    )?.length || 0;

  return (
    <div className="preview">
      {usersData?.length ? (
        <Box
          padding={0}
          title={<div className="title">{messages.commonUsers}</div>}
          subHeaderContent={
            <FlexContainer style={{ gap: 10 }}>
              <div className="summary-item">
                <StatCount
                  category="success-light"
                  isLarge={true}
                  label={messages.commonNewUsers}
                  value={validRows}
                />
              </div>
              {updateRows > 0 ? (
                <div className="summary-item">
                  <StatCount
                    category="warning-light"
                    isLarge={true}
                    label={messages.commonUpdatedUsers}
                    value={updateRows}
                  />
                </div>
              ) : null}
              {ignoreRows > 0 ? (
                <div className="summary-item">
                  <StatCount
                    isLarge={true}
                    label={messages.usersImportIgnoreRows}
                    value={ignoreRows}
                  />
                </div>
              ) : null}
              {invalidRows > 0 ? (
                <div className="summary-item">
                  <StatCount
                    category="error-light"
                    isLarge={true}
                    label={messages.usersImportInvalidRows}
                    value={invalidRows}
                  />
                </div>
              ) : null}
            </FlexContainer>
          }
        >
          <div className="users" style={{ overflowY: 'auto' }}>
            <BoxTable>
              <BoxTableHeader
                hideAtBreakpoint={breakpoints.medium}
                style={{ whiteSpace: 'nowrap' }}
              >
                <BoxTableCell>{messages.commonStatus}</BoxTableCell>
                {accountType === 'email' ? (
                  <BoxTableCell>{messages.commonEmail}</BoxTableCell>
                ) : null}
                <BoxTableCell>{messages.commonUsername}</BoxTableCell>
                <BoxTableCell>{messages.usersFirstName}</BoxTableCell>
                <BoxTableCell>{messages.usersLastName}</BoxTableCell>
                {userCustomAttributes.map((attribute) => (
                  <BoxTableCell key={attribute.key}>
                    {attribute.titleTranslations?.[language] || attribute.title}
                  </BoxTableCell>
                ))}
              </BoxTableHeader>

              {usersData.map((user, userIndex) => (
                <BoxTableRow key={userIndex}>
                  <BoxTableCell cellBreakpoint={breakpoints.medium}>
                    {user.status === 'ok' ? (
                      <Badge
                        category="success-light"
                        text={messages.commonNew}
                      />
                    ) : user.status === 'update' ? (
                      <Badge
                        category="warning-light"
                        text={messages.commonUpdate}
                      />
                    ) : user.status === 'ignore' ? (
                      <Badge text={messages.commonIgnore} />
                    ) : user.status === 'duplicate' ? (
                      <Badge text={messages.commonDuplicate} />
                    ) : (
                      <Badge
                        category="error-light"
                        text={messages.usersImportInvalid}
                        tooltip={user.error}
                      />
                    )}
                  </BoxTableCell>
                  {accountType === 'email' ? (
                    <BoxTableCell cellBreakpoint={breakpoints.medium}>
                      {user.email}
                    </BoxTableCell>
                  ) : null}
                  <BoxTableCell cellBreakpoint={breakpoints.medium}>
                    {user.username}
                  </BoxTableCell>
                  <BoxTableCell cellBreakpoint={breakpoints.medium}>
                    {user.firstName}
                  </BoxTableCell>
                  <BoxTableCell cellBreakpoint={breakpoints.medium}>
                    {user.lastName}
                  </BoxTableCell>
                  {userCustomAttributes.map((attribute) => (
                    <BoxTableCell
                      key={attribute.key}
                      cellBreakpoint={breakpoints.medium}
                    >
                      <div style={{ whiteSpace: 'nowrap' }}>
                        {user.customAttributes?.[attribute.key] ===
                        undefined ? (
                          <span
                            style={{ fontStyle: 'italic', fontWeight: 400 }}
                          >
                            {messages.commonIgnored}
                          </span>
                        ) : attribute.type === 'Array' ||
                          attribute.type === 'ArrayWithOptions' ? (
                          <span>
                            {(
                              user.customAttributes?.[attribute.key] as string[]
                            )?.join(', ') || ''}
                          </span>
                        ) : attribute.type === 'Boolean' ? (
                          <span>
                            {Boolean(user.customAttributes?.[attribute.key])
                              ? messages.commonYes
                              : messages.commonNo}
                          </span>
                        ) : (
                          <span>
                            {user.customAttributes?.[attribute.key] || ''}
                          </span>
                        )}
                      </div>
                    </BoxTableCell>
                  ))}
                </BoxTableRow>
              ))}
            </BoxTable>
          </div>
        </Box>
      ) : (
        <EmptyIndicator icon="user" title={messages.usersNoUsersFound} />
      )}
    </div>
  );
};

export default UsersImportCsvPreview;
